/**
 * Environment Management Store
 * Zustand store for managing environments and deployments
 */

import { create } from 'zustand';
import type {
  Environment,
  CreateEnvironmentInput,
  UpdateEnvironmentInput,
  EnvironmentSecret,
  AgentDeployment,
  CreateDeploymentInput,
  DeploymentDiff,
} from './types';
import * as api from './api';

interface EnvironmentState {
  // Environments
  environments: Environment[];
  environmentsLoading: boolean;
  environmentsError: string | null;
  selectedEnvironment: Environment | null;

  // Secrets
  secrets: EnvironmentSecret[];
  secretsLoading: boolean;

  // Deployments
  deployments: AgentDeployment[];
  deploymentsLoading: boolean;
  deploymentsError: string | null;
  selectedDeployment: AgentDeployment | null;
  deploymentDiff: DeploymentDiff | null;

  // Pagination
  deploymentsPagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };

  // Actions - Environments
  loadEnvironments: (params?: { type?: string; status?: string }) => Promise<void>;
  loadEnvironment: (id: string) => Promise<Environment>;
  createEnvironment: (input: CreateEnvironmentInput) => Promise<Environment>;
  updateEnvironment: (id: string, input: UpdateEnvironmentInput) => Promise<Environment>;
  deleteEnvironment: (id: string) => Promise<void>;
  setSelectedEnvironment: (env: Environment | null) => void;

  // Actions - Secrets
  loadSecrets: (environmentId: string) => Promise<void>;
  setSecret: (environmentId: string, key: string, value: string, description?: string) => Promise<void>;
  deleteSecret: (environmentId: string, key: string) => Promise<void>;

  // Actions - Deployments
  loadDeployments: (params?: {
    agent_id?: string;
    environment_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) => Promise<void>;
  loadDeployment: (id: string) => Promise<AgentDeployment>;
  createDeployment: (input: CreateDeploymentInput) => Promise<AgentDeployment>;
  approveDeployment: (id: string, comment?: string) => Promise<void>;
  rejectDeployment: (id: string, comment: string) => Promise<void>;
  rollbackDeployment: (id: string, reason: string) => Promise<void>;
  promoteDeployment: (id: string, targetEnvironmentId: string) => Promise<AgentDeployment>;
  loadDeploymentDiff: (id: string, compareToVersion?: number) => Promise<void>;
  setSelectedDeployment: (deployment: AgentDeployment | null) => void;

  clearErrors: () => void;
}

export const useEnvironmentStore = create<EnvironmentState>((set, get) => ({
  // Initial state
  environments: [],
  environmentsLoading: false,
  environmentsError: null,
  selectedEnvironment: null,

  secrets: [],
  secretsLoading: false,

  deployments: [],
  deploymentsLoading: false,
  deploymentsError: null,
  selectedDeployment: null,
  deploymentDiff: null,

  deploymentsPagination: {
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false,
  },

  // Environment Actions
  loadEnvironments: async (params) => {
    set({ environmentsLoading: true, environmentsError: null });
    try {
      const response = await api.listEnvironments(params);
      set({ environments: response.data, environmentsLoading: false });
    } catch (error) {
      set({
        environmentsError: (error as Error).message,
        environmentsLoading: false,
      });
    }
  },

  loadEnvironment: async (id: string) => {
    try {
      const response = await api.getEnvironment(id);
      set({ selectedEnvironment: response.data });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createEnvironment: async (input: CreateEnvironmentInput) => {
    set({ environmentsLoading: true, environmentsError: null });
    try {
      const response = await api.createEnvironment(input);
      set((state) => ({
        environments: [...state.environments, response.data],
        environmentsLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({
        environmentsError: (error as Error).message,
        environmentsLoading: false,
      });
      throw error;
    }
  },

  updateEnvironment: async (id: string, input: UpdateEnvironmentInput) => {
    set({ environmentsLoading: true, environmentsError: null });
    try {
      const response = await api.updateEnvironment(id, input);
      set((state) => ({
        environments: state.environments.map((env) =>
          env.id === id ? response.data : env
        ),
        selectedEnvironment:
          state.selectedEnvironment?.id === id ? response.data : state.selectedEnvironment,
        environmentsLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({
        environmentsError: (error as Error).message,
        environmentsLoading: false,
      });
      throw error;
    }
  },

  deleteEnvironment: async (id: string) => {
    set({ environmentsLoading: true, environmentsError: null });
    try {
      await api.deleteEnvironment(id);
      set((state) => ({
        environments: state.environments.filter((env) => env.id !== id),
        selectedEnvironment:
          state.selectedEnvironment?.id === id ? null : state.selectedEnvironment,
        environmentsLoading: false,
      }));
    } catch (error) {
      set({
        environmentsError: (error as Error).message,
        environmentsLoading: false,
      });
      throw error;
    }
  },

  setSelectedEnvironment: (env: Environment | null) => {
    set({ selectedEnvironment: env });
  },

  // Secret Actions
  loadSecrets: async (environmentId: string) => {
    set({ secretsLoading: true });
    try {
      const response = await api.listSecrets(environmentId);
      set({ secrets: response.data, secretsLoading: false });
    } catch (error) {
      set({ secretsLoading: false });
      throw error;
    }
  },

  setSecret: async (environmentId: string, key: string, value: string, description?: string) => {
    try {
      await api.setSecret(environmentId, key, value, description);
      // Reload secrets
      get().loadSecrets(environmentId);
    } catch (error) {
      throw error;
    }
  },

  deleteSecret: async (environmentId: string, key: string) => {
    try {
      await api.deleteSecret(environmentId, key);
      set((state) => ({
        secrets: state.secrets.filter((s) => s.key !== key),
      }));
    } catch (error) {
      throw error;
    }
  },

  // Deployment Actions
  loadDeployments: async (params) => {
    set({ deploymentsLoading: true, deploymentsError: null });
    try {
      const response = await api.listDeployments(params);
      set({
        deployments: response.data,
        deploymentsPagination: response.pagination,
        deploymentsLoading: false,
      });
    } catch (error) {
      set({
        deploymentsError: (error as Error).message,
        deploymentsLoading: false,
      });
    }
  },

  loadDeployment: async (id: string) => {
    try {
      const response = await api.getDeployment(id);
      set({ selectedDeployment: response.data });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createDeployment: async (input: CreateDeploymentInput) => {
    set({ deploymentsLoading: true, deploymentsError: null });
    try {
      const response = await api.createDeployment(input);
      set((state) => ({
        deployments: [response.data, ...state.deployments],
        deploymentsLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({
        deploymentsError: (error as Error).message,
        deploymentsLoading: false,
      });
      throw error;
    }
  },

  approveDeployment: async (id: string, comment?: string) => {
    try {
      await api.approveDeployment(id, comment);
      // Reload deployment
      get().loadDeployment(id);
    } catch (error) {
      throw error;
    }
  },

  rejectDeployment: async (id: string, comment: string) => {
    try {
      await api.rejectDeployment(id, comment);
      // Reload deployment
      get().loadDeployment(id);
    } catch (error) {
      throw error;
    }
  },

  rollbackDeployment: async (id: string, reason: string) => {
    try {
      await api.rollbackDeployment(id, reason);
      // Reload deployments
      get().loadDeployments();
    } catch (error) {
      throw error;
    }
  },

  promoteDeployment: async (id: string, targetEnvironmentId: string) => {
    try {
      const response = await api.promoteDeployment(id, targetEnvironmentId);
      // Add the new deployment to the list
      set((state) => ({
        deployments: [response.data, ...state.deployments],
      }));
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  loadDeploymentDiff: async (id: string, compareToVersion?: number) => {
    try {
      const response = await api.getDeploymentDiff(id, compareToVersion);
      set({ deploymentDiff: response.data });
    } catch (error) {
      throw error;
    }
  },

  setSelectedDeployment: (deployment: AgentDeployment | null) => {
    set({ selectedDeployment: deployment, deploymentDiff: null });
  },

  clearErrors: () => {
    set({ environmentsError: null, deploymentsError: null });
  },
}));

// Selectors
export const useEnvironments = () => useEnvironmentStore((state) => state.environments);
export const useSelectedEnvironment = () => useEnvironmentStore((state) => state.selectedEnvironment);
export const useDeployments = () => useEnvironmentStore((state) => state.deployments);
export const useSelectedDeployment = () => useEnvironmentStore((state) => state.selectedDeployment);
export const useEnvironmentSecrets = () => useEnvironmentStore((state) => state.secrets);

// Environment by type selector
export const useEnvironmentsByType = (type: string) =>
  useEnvironmentStore((state) => state.environments.filter((env) => env.type === type));
